/**
 * Console admin des CAMPAGNES du Live School : crée/édite des campagnes (titre,
 * sous-titre, description, début/fin, active) et leurs slots (niveau × matière ×
 * cadence). Le seed (`live-campaign-seed`) matérialise les défis. PostgREST direct
 * + RLS admin (hooks `@/lib/live-campaigns`). Mobile-first (cf. CLAUDE.md §2).
 */

import { useState } from 'react';
import {
  SCHOOL_LEVELS,
  SECONDARY_SCHOOL_LEVELS,
  SUBJECTS,
  type SchoolLevel,
  type SubjectSlug,
} from '@jeraisonne/db';
import {
  campaignLifecycle,
  formatFrequency,
  FREQUENCY_UNITS,
  type CampaignLifecycle,
  type FrequencyUnit,
} from '@jeraisonne/db/live';
import { Button, Card, Input, Label } from '@jeraisonne/ui';
import { IllustrationEditor } from '@/components/illustration-editor';
import { LIVE_COVER_PRESETS, liveCoverUrl, presetPath } from '@/lib/live-covers';
import {
  useLiveCampaigns,
  useSaveCampaign,
  useDeleteCampaign,
  useSaveSlot,
  useDeleteSlot,
  useApplyCampaignTime,
  useSetCampaignCover,
  isoToLocalInput,
  localInputToIso,
  campaignKind,
  type LiveCampaign,
  type LiveCampaignKind,
  type LiveCampaignSlot,
} from '@/lib/live-campaigns';
import { localTimeOfDay, parseTimeOfDay } from '@/lib/live-campaign-reconcile';

const LEVEL_LABEL = Object.fromEntries(SCHOOL_LEVELS.map((l) => [l.value, l.label]));
const SUBJECT_LABEL = Object.fromEntries(SUBJECTS.map((s) => [s.value, s.label]));

const dateFmt = new Intl.DateTimeFormat('fr-FR', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

const LIFECYCLE: Record<CampaignLifecycle, { label: string; className: string }> = {
  active: { label: 'Active', className: 'bg-success-100 text-success-700' },
  upcoming: { label: 'À venir', className: 'bg-accent-100 text-accent-700' },
  ended: { label: 'Terminée', className: 'bg-ink-100 text-ink-500' },
  inactive: { label: 'Désactivée', className: 'bg-ink-100 text-ink-500' },
};

const KIND_LABEL: Record<LiveCampaignKind, string> = {
  slots: 'Créneaux',
  sunday: 'Dimanche',
  special: 'Spécial vacances',
};

const specialDateFmt = new Intl.DateTimeFormat('fr-FR', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});

/** « samedi 31 octobre 2026 dès 14 h 00, top 10 » (spécial des vacances). */
export function specialScheduleLabel(c: {
  special_date: string | null;
  special_time: string;
  prize_top_n: number | null;
}): string {
  if (!c.special_date) return 'Date à choisir';
  const [h, m] = c.special_time.split(':');
  const day = specialDateFmt.format(new Date(`${c.special_date}T12:00:00`));
  return `${day} dès ${Number(h)} h ${m ?? '00'}, top ${c.prize_top_n ?? 10}`;
}

const inputClass =
  'border-ink-200 min-h-11 w-full rounded-xl border bg-white px-3 text-sm focus:border-accent-400 focus:outline-none';

export function LiveCampaignsPanel() {
  const { data: campaigns, isLoading } = useLiveCampaigns();
  const [creating, setCreating] = useState(false);

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-ink-500 text-sm">
          Les campagnes pilotent la création des défis. La landing et la page <code>/live</code> ne
          montrent que les campagnes actives.
        </p>
        {!creating && (
          <Button type="button" size="sm" fullWidth onClick={() => setCreating(true)}>
            Nouvelle campagne
          </Button>
        )}
      </div>

      {creating && (
        <Card className="p-4">
          <CampaignForm onDone={() => setCreating(false)} />
        </Card>
      )}

      {isLoading ? (
        <p className="text-ink-500 text-sm">Chargement des campagnes…</p>
      ) : (campaigns ?? []).length === 0 ? (
        <p className="text-ink-500 text-sm">
          Aucune campagne. Crée-en une pour programmer des défis.
        </p>
      ) : (
        (campaigns ?? []).map((c) => <CampaignCard key={c.id} campaign={c} />)
      )}
    </div>
  );
}

function CampaignCard({ campaign }: { campaign: LiveCampaign }) {
  const [editing, setEditing] = useState(false);
  const save = useSaveCampaign();
  const del = useDeleteCampaign();
  const lifecycle = LIFECYCLE[campaignLifecycle(campaign, Date.now())];
  const kind = campaignKind(campaign);

  return (
    <Card className="p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-ink-900 text-base font-bold">{campaign.title}</h3>
            <span
              className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${lifecycle.className}`}
            >
              {lifecycle.label}
            </span>
            <span className="bg-primary-50 text-primary-700 rounded-full px-2 py-0.5 text-[11px] font-semibold">
              {KIND_LABEL[kind]}
            </span>
          </div>
          {campaign.subtitle && <p className="text-ink-600 text-sm">{campaign.subtitle}</p>}
          <p className="text-ink-400 mt-1 text-xs">
            {dateFmt.format(new Date(campaign.starts_at))}
            {campaign.ends_at ? ` → ${dateFmt.format(new Date(campaign.ends_at))}` : ' → (ouvert)'}
          </p>
        </div>
      </div>

      {editing ? (
        <div className="mt-3">
          <CampaignForm campaign={campaign} onDone={() => setEditing(false)} />
        </div>
      ) : (
        <>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => setEditing(true)}>
              Modifier
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={save.isPending}
              onClick={() =>
                save.mutate({
                  id: campaign.id,
                  title: campaign.title,
                  subtitle: campaign.subtitle,
                  description: campaign.description,
                  starts_at: campaign.starts_at,
                  ends_at: campaign.ends_at,
                  is_active: !campaign.is_active,
                  step_duration_ms: campaign.step_duration_ms,
                })
              }
            >
              {campaign.is_active ? 'Désactiver' : 'Activer'}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={del.isPending}
              onClick={() => {
                if (
                  confirm(
                    `Supprimer la campagne « ${campaign.title} » ? Les défis à venir non commencés seront annulés.`,
                  )
                )
                  del.mutate(campaign.id);
              }}
            >
              Supprimer
            </Button>
          </div>

          <div className="mt-3">
            <IllustrationEditor
              key={campaign.illustration_url ?? 'none'}
              entityType="live_campaign"
              entityId={campaign.id}
              initialUrl={campaign.illustration_url}
              initialPrompt={campaign.illustration_prompt}
              title={campaign.title}
              label="Couverture"
            />
            <CampaignCoverPresets campaign={campaign} />
          </div>

          {kind === 'slots' ? (
            <>
              <CommonTimeControl campaign={campaign} />
              <SlotEditor campaign={campaign} />
            </>
          ) : (
            <p className="text-ink-600 border-ink-100 mt-3 rounded-xl border bg-white p-3 text-sm">
              {kind === 'sunday'
                ? 'Chaque dimanche dès 10 h : un live par programme, toutes les 15 min (générés 14 jours à l’avance).'
                : `Grande journée : ${specialScheduleLabel(campaign)}. Un live par programme toutes les 15 min, générés 45 jours avant.`}
            </p>
          )}
        </>
      )}
    </Card>
  );
}

function CommonTimeControl({ campaign }: { campaign: LiveCampaign }) {
  const apply = useApplyCampaignTime();
  const [time, setTime] = useState(() => localTimeOfDay(campaign.starts_at));
  const parsed = parseTimeOfDay(time);

  return (
    <div className="border-ink-100 mt-3 rounded-xl border bg-white p-3">
      <p className="text-ink-600 mb-1 text-xs font-semibold uppercase tracking-wide">
        Heure commune des défis
      </p>
      <p className="text-ink-400 mb-2 text-xs">
        Décale l'heure de tous les défis programmés à venir. Les dates et les exercices épinglés
        sont conservés.
      </p>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <input
          type="time"
          className={`${inputClass} sm:max-w-40`}
          value={time}
          onChange={(e) => setTime(e.target.value)}
          aria-label="Heure des défis"
        />
        <Button
          type="button"
          size="sm"
          fullWidth
          disabled={!parsed || apply.isPending}
          onClick={() =>
            parsed &&
            apply.mutate({ campaignId: campaign.id, hour: parsed.hour, minute: parsed.minute })
          }
        >
          {apply.isPending ? 'Application…' : 'Appliquer à tous les défis'}
        </Button>
      </div>
      {apply.isSuccess && (
        <p className="text-success-600 mt-1 text-xs">
          Heure appliquée à {apply.data} défi{(apply.data ?? 0) > 1 ? 's' : ''} programmé
          {(apply.data ?? 0) > 1 ? 's' : ''}.
        </p>
      )}
      {apply.isError && (
        <p className="text-error-600 mt-1 text-xs">
          Échec : un créneau est peut-être déjà pris à cette heure. Réessaie.
        </p>
      )}
    </div>
  );
}

function CampaignForm({ campaign, onDone }: { campaign?: LiveCampaign; onDone: () => void }) {
  const save = useSaveCampaign();
  const [title, setTitle] = useState(campaign?.title ?? '');
  const [subtitle, setSubtitle] = useState(campaign?.subtitle ?? '');
  const [description, setDescription] = useState(campaign?.description ?? '');
  const [startsAt, setStartsAt] = useState(
    isoToLocalInput(campaign?.starts_at ?? new Date().toISOString()),
  );
  const [endsAt, setEndsAt] = useState(isoToLocalInput(campaign?.ends_at ?? null));
  const [isActive, setIsActive] = useState(campaign?.is_active ?? true);
  // Durée par question, saisie en SECONDES (défaut 1 min). Chaîne pour tolérer
  // un champ vidé pendant la frappe ; validée à la soumission.
  const [kind, setKind] = useState<LiveCampaignKind>(campaign ? campaignKind(campaign) : 'slots');
  const [specialDate, setSpecialDate] = useState(campaign?.special_date ?? '');
  const [specialTime, setSpecialTime] = useState((campaign?.special_time ?? '14:00').slice(0, 5));
  const [topN, setTopN] = useState(String(campaign?.prize_top_n ?? 10));
  const [stepSeconds, setStepSeconds] = useState(
    String(Math.round((campaign?.step_duration_ms ?? 60000) / 1000)),
  );

  const startIso = localInputToIso(startsAt);
  const endIso = localInputToIso(endsAt);
  const stepSec = Number(stepSeconds);
  const stepValid = Number.isFinite(stepSec) && stepSec >= 5 && stepSec <= 600;
  const topNum = Number(topN);
  const specialInvalid =
    kind === 'special' &&
    (!/^\d{4}-\d{2}-\d{2}$/.test(specialDate) ||
      !/^\d{2}:\d{2}$/.test(specialTime) ||
      !Number.isInteger(topNum) ||
      topNum < 1 ||
      topNum > 10);
  const invalid =
    specialInvalid ||
    !title.trim() ||
    !startIso ||
    !stepValid ||
    (endIso != null && startIso != null && endIso <= startIso);

  function submit() {
    if (invalid || !startIso) return;
    save.mutate(
      {
        id: campaign?.id,
        title: title.trim(),
        subtitle: subtitle.trim() || null,
        description: description.trim() || null,
        starts_at: startIso,
        ends_at: endIso,
        is_active: isActive,
        step_duration_ms: Math.round(stepSec) * 1000,
        ...(kind === 'special'
          ? {
              kind,
              special_date: specialDate,
              special_time: specialTime,
              prize_top_n: topNum,
            }
          : campaign
            ? {}
            : { kind }),
      },
      { onSuccess: onDone },
    );
  }

  return (
    <div className="space-y-3">
      {!campaign && (
        <div className="space-y-1">
          <Label htmlFor="camp-kind">Type</Label>
          <select
            id="camp-kind"
            className={inputClass}
            value={kind}
            onChange={(e) => setKind(e.target.value as LiveCampaignKind)}
          >
            <option value="slots">Campagne à créneaux (niveaux × cadence)</option>
            <option value="special">Spécial vacances (une grande journée)</option>
          </select>
        </div>
      )}
      {kind === 'special' && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div className="space-y-1">
            <Label htmlFor="camp-special-date">Jour du spécial</Label>
            <input
              id="camp-special-date"
              type="date"
              className={inputClass}
              value={specialDate}
              onChange={(e) => setSpecialDate(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="camp-special-time">1er live à</Label>
            <input
              id="camp-special-time"
              type="time"
              className={inputClass}
              value={specialTime}
              onChange={(e) => setSpecialTime(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="camp-special-top">Gagnants par live</Label>
            <input
              id="camp-special-top"
              type="number"
              min={1}
              max={10}
              className={inputClass}
              value={topN}
              onChange={(e) => setTopN(e.target.value)}
            />
          </div>
        </div>
      )}
      <div className="space-y-1">
        <Label htmlFor="camp-title">Titre</Label>
        <Input
          id="camp-title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Live School"
        />
      </div>
      <div className="space-y-1">
        <Label htmlFor="camp-subtitle">Sous-titre</Label>
        <Input
          id="camp-subtitle"
          value={subtitle}
          onChange={(e) => setSubtitle(e.target.value)}
          placeholder="Le rendez-vous hebdomadaire de tous les élèves"
        />
      </div>
      <div className="space-y-1">
        <Label htmlFor="camp-desc">Description</Label>
        <textarea
          id="camp-desc"
          className={`${inputClass} min-h-24 py-2`}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Explique l'événement aux élèves…"
        />
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor="camp-start">Début</Label>
          <input
            id="camp-start"
            type="datetime-local"
            className={inputClass}
            value={startsAt}
            onChange={(e) => setStartsAt(e.target.value)}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="camp-end">Fin (optionnelle)</Label>
          <input
            id="camp-end"
            type="datetime-local"
            className={inputClass}
            value={endsAt}
            onChange={(e) => setEndsAt(e.target.value)}
          />
        </div>
      </div>
      <div className="space-y-1">
        <Label htmlFor="camp-step-duration">Durée par question (secondes)</Label>
        <input
          id="camp-step-duration"
          type="number"
          min={5}
          max={600}
          step={5}
          className={inputClass}
          value={stepSeconds}
          onChange={(e) => setStepSeconds(e.target.value)}
        />
        <p className="text-ink-400 text-xs">
          Temps laissé pour répondre à chaque question (défaut 60 s). Appliqué aux défis à venir de
          cette campagne.
        </p>
      </div>

      <label className="flex min-h-11 items-center gap-2">
        <input
          type="checkbox"
          className="h-5 w-5"
          checked={isActive}
          onChange={(e) => setIsActive(e.target.checked)}
        />
        <span className="text-ink-700 text-sm">
          Campagne active (visible sur la landing et /live)
        </span>
      </label>

      {save.isError && <p className="text-error-600 text-sm">Échec de l'enregistrement.</p>}

      <div className="flex flex-col gap-2 sm:flex-row">
        <Button type="button" fullWidth disabled={invalid || save.isPending} onClick={submit}>
          {save.isPending ? 'Enregistrement…' : campaign ? 'Enregistrer' : 'Créer la campagne'}
        </Button>
        <Button type="button" variant="ghost" fullWidth onClick={onDone}>
          Annuler
        </Button>
      </div>
    </div>
  );
}

function SlotEditor({ campaign }: { campaign: LiveCampaign }) {
  const saveSlot = useSaveSlot();
  const delSlot = useDeleteSlot();
  const [level, setLevel] = useState<SchoolLevel>('6e');
  const [subject, setSubject] = useState<string>(''); // '' = toutes matières
  const [count, setCount] = useState(1);
  const [unit, setUnit] = useState<FrequencyUnit>('week');

  // Matières valides pour le niveau choisi + l'option « Toutes matières ».
  const subjectOptions = SUBJECTS.filter((s) => s.levels.includes(level));

  function add() {
    saveSlot.mutate({
      campaign_id: campaign.id,
      level,
      subject: subject ? (subject as SubjectSlug) : null,
      frequency_count: Math.max(1, count),
      frequency_unit: unit,
    });
  }

  return (
    <div className="border-ink-100 mt-3 rounded-xl border bg-white p-3">
      <p className="text-ink-600 mb-2 text-xs font-semibold uppercase tracking-wide">
        Niveaux × matières ({campaign.live_campaign_slots.length})
      </p>

      {campaign.live_campaign_slots.length > 0 && (
        <ul className="mb-3 space-y-1.5">
          {campaign.live_campaign_slots.map((s) => (
            <SlotRow
              key={s.id}
              slot={s}
              onDelete={() => delSlot.mutate(s.id)}
              deleting={delSlot.isPending}
            />
          ))}
        </ul>
      )}

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <select
          className={inputClass}
          value={level}
          onChange={(e) => {
            setLevel(e.target.value as SchoolLevel);
            setSubject('');
          }}
          aria-label="Niveau"
        >
          {SECONDARY_SCHOOL_LEVELS.map((l) => (
            <option key={l.value} value={l.value}>
              {l.label}
            </option>
          ))}
        </select>
        <select
          className={inputClass}
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          aria-label="Matière"
        >
          <option value="">Toutes matières</option>
          {subjectOptions.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>
        <input
          type="number"
          min={1}
          className={inputClass}
          value={count}
          onChange={(e) => setCount(Number(e.target.value) || 1)}
          aria-label="Fréquence (nombre)"
        />
        <select
          className={inputClass}
          value={unit}
          onChange={(e) => setUnit(e.target.value as FrequencyUnit)}
          aria-label="Fréquence (unité)"
        >
          {FREQUENCY_UNITS.map((u) => (
            <option key={u.value} value={u.value}>
              {count <= 1 ? u.label : u.labelPlural}
            </option>
          ))}
        </select>
      </div>
      <Button
        type="button"
        size="sm"
        fullWidth
        className="mt-2"
        disabled={saveSlot.isPending}
        onClick={add}
      >
        {saveSlot.isPending ? 'Ajout…' : `Ajouter — ${formatFrequency(count, unit)}`}
      </Button>
      {saveSlot.isError && (
        <p className="text-error-600 mt-1 text-xs">
          Ce couple (niveau, matière) existe déjà dans la campagne.
        </p>
      )}
    </div>
  );
}

function SlotRow({
  slot,
  onDelete,
  deleting,
}: {
  slot: LiveCampaignSlot;
  onDelete: () => void;
  deleting: boolean;
}) {
  return (
    <li className="bg-ink-50 flex items-center justify-between gap-2 rounded-lg px-3 py-2">
      <span className="text-ink-800 text-sm">
        <span className="font-semibold">{LEVEL_LABEL[slot.level] ?? slot.level}</span>
        {' · '}
        {slot.subject ? (SUBJECT_LABEL[slot.subject] ?? slot.subject) : 'Toutes matières'}
        {' — '}
        <span className="text-ink-500">
          {formatFrequency(slot.frequency_count, slot.frequency_unit)}
        </span>
      </span>
      <button
        type="button"
        className="text-ink-400 hover:text-error-600 min-h-11 min-w-11 text-lg leading-none transition-colors"
        disabled={deleting}
        onClick={onDelete}
        aria-label="Retirer ce slot"
      >
        ×
      </button>
    </li>
  );
}

/** Covers prédéfinies des lives : un toucher les pose comme couverture. */
function CampaignCoverPresets({ campaign }: { campaign: LiveCampaign }) {
  const setCover = useSetCampaignCover();
  return (
    <div className="mt-2">
      <p className="text-ink-500 mb-1.5 text-xs font-semibold">Ou une cover prédéfinie</p>
      <div className="flex gap-2 overflow-x-auto pb-1">
        {LIVE_COVER_PRESETS.map((p) => {
          const url = liveCoverUrl({ path: presetPath(p.key) });
          const selected = campaign.illustration_url === url;
          return (
            <button
              key={p.key}
              type="button"
              aria-label={`Cover « ${p.label} »`}
              aria-pressed={selected}
              disabled={setCover.isPending}
              onClick={() => setCover.mutate({ campaignId: campaign.id, url })}
              className={`h-12 w-16 shrink-0 overflow-hidden rounded-lg transition active:scale-[0.98] ${
                selected ? 'ring-[3px] ring-[#f4c64e]' : 'ring-1 ring-black/10'
              }`}
            >
              <img
                src={liveCoverUrl({ path: presetPath(p.key), thumb: true })}
                alt=""
                className="h-full w-full object-cover"
              />
            </button>
          );
        })}
      </div>
      {setCover.isError && (
        <p role="alert" className="text-error-600 mt-1 text-xs">
          La cover n’a pas pu être enregistrée.
        </p>
      )}
    </div>
  );
}
